import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AccountPeopleAPI } from 'api/account-people';
import { AccountPeopleProfile } from 'api/account-people/profile-type';
import {
  GetSignalOccurrenceGroupedForUserPayload,
  PeopleListRow,
  PeopleListV2,
  SignalOccurrenceGroupedForUser,
  SignalOccurrenceGroupedForUserPaginated,
} from 'api/account-people/types';

const ACCOUNT_PEOPLE_LIST_QUERY_IDENTIFIER = 'account-people-list';

export const getAccountPeopleListQueryKey = (accountId: number): string[] => [
  ACCOUNT_PEOPLE_LIST_QUERY_IDENTIFIER,
  accountId.toString(),
];

const ACCOUNT_PEOPLE_PROFILE_QUERY_IDENTIFIER = 'account-people-profile';

export const getAccountPeopleProfileQueryKey = (userId: number): string[] => [
  ACCOUNT_PEOPLE_PROFILE_QUERY_IDENTIFIER,
  userId.toString(),
];

const ACCOUNT_PEOPLE_V2_QUERY_IDENTIFIER = 'account-people-v2';

export const getAccountPeopleV2QueryKey = (accountId: number): string[] => [
  ACCOUNT_PEOPLE_V2_QUERY_IDENTIFIER,
  accountId.toString(),
];

export const useAccountPeopleListQuery = (
  accountId: number,
  options?: Omit<UseQueryOptions<PeopleListRow[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PeopleListRow[]>({
    queryKey: getAccountPeopleListQueryKey(accountId),
    queryFn: () => AccountPeopleAPI.getPeopleList(accountId),
    ...options,
  });
};

export const useAccountPeopleV2Query = (
  accountId: number,
  options?: Omit<UseQueryOptions<PeopleListV2>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PeopleListV2>({
    queryKey: getAccountPeopleV2QueryKey(accountId),
    queryFn: () => AccountPeopleAPI.getPeopleV2(accountId),
    ...options,
  });
};

export const useAccountPeopleProfileQuery = (
  userId: number,
  options?: Omit<UseQueryOptions<AccountPeopleProfile>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<AccountPeopleProfile>({
    queryKey: getAccountPeopleProfileQueryKey(userId),
    queryFn: () => AccountPeopleAPI.getPeopleProfile(userId),
    ...options,
  });
};
type GetSignalOccurrenceGroupedForUserPaginatedPayload = Omit<
  GetSignalOccurrenceGroupedForUserPayload,
  'limit' | 'offset'
>;
export const getSignalOccurrencesGroupedForUserQueryKey = (
  payload: GetSignalOccurrenceGroupedForUserPaginatedPayload,
): string[] => [
  'account-people-signal-occurrences-grouped',
  payload.signalType,
  payload.userId.toString(),
  payload.from,
  payload.to,
  payload.sort ?? 'signalFrequency.desc',
];

const PAGE_SIZE = 10;

export const useGetSignalOccurrencesGroupedForUserPaginatedQuery = (
  payload: GetSignalOccurrenceGroupedForUserPaginatedPayload,
  options?: Omit<
    UseInfiniteQueryOptions<
      SignalOccurrenceGroupedForUserPaginated,
      unknown,
      SignalOccurrenceGroupedForUser[],
      SignalOccurrenceGroupedForUserPaginated,
      ReturnType<typeof getSignalOccurrencesGroupedForUserQueryKey>,
      { offset: number }
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<
    SignalOccurrenceGroupedForUserPaginated,
    unknown,
    SignalOccurrenceGroupedForUser[],
    ReturnType<typeof getSignalOccurrencesGroupedForUserQueryKey>,
    { offset: number }
  >({
    queryKey: getSignalOccurrencesGroupedForUserQueryKey(payload),
    initialPageParam: { offset: 0 },
    getNextPageParam: (lastPage, pages) => {
      const totalItems = pages.reduce((acc, page) => acc + page.data.length, 0);

      if (lastPage && totalItems < lastPage.count) {
        return { offset: totalItems };
      }

      return undefined;
    },
    queryFn: ({ pageParam = { offset: 0, limit: PAGE_SIZE } }) =>
      AccountPeopleAPI.getSignalOccurrenceGroupedForUser({
        ...payload,
        offset: pageParam.offset,
        limit: PAGE_SIZE,
      }),
    select: (data) => data.pages.flatMap((page) => page.data),
    ...options,
  });
};
